"use client";

import React, { useEffect, useRef } from "react";
import { stegaClean } from "@sanity/client/stega";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageTrademania2Schema } from "@/sanity/pages/PageTrademania2Template";
import {
  Chip,
  FadeUp,
  Highlight,
  RevealWords,
  pad,
  prefersReducedMotion,
  revealLines,
  splitFirstWord,
} from "@/components/trademania-2/shared";
import { PlatformVisual, RevealVisual } from "@/components/trademania-2/CardVisuals";
import { ToolsVisual } from "@/components/trademania-2/ToolsVisual";

type Card = NonNullable<PageTrademania2Schema["featuresCards"]>[number];

function CardVisual({ card, target }: { card: Card; target?: string }) {
  switch (stegaClean(card?.visual)) {
    case "tools":
      return <ToolsVisual className="w-full rounded-[4px]" />;
    case "reveal":
      return (
        <RevealVisual
          className="w-full"
          label={card?.revealLabel}
          date={card?.revealDate}
          target={target}
        />
      );
    default:
      return (
        <PlatformVisual
          className="w-full"
          image={card?.image}
          video={card?.video}
        />
      );
  }
}

export default function Features({ data }: { data: PageTrademania2Schema }) {
  const cardsRef = useRef<HTMLDivElement>(null);

  // the card closest to the viewport centre is full size, the others shrink (Figma: 1200 vs 1116)
  useEffect(() => {
    const wrap = cardsRef.current;
    if (!wrap || prefersReducedMotion()) return;
    const cards = Array.from(wrap.children) as HTMLElement[];
    let raf = 0;
    const loop = () => {
      const mid = window.innerHeight / 2;
      const desktop = window.innerWidth >= 993;
      cards.forEach((card) => {
        const r = card.getBoundingClientRect();
        const d = Math.min(1, (Math.abs(r.top + r.height / 2 - mid) / window.innerHeight) * 1.6);
        card.style.transform = desktop ? `scale(${(1 - 0.07 * d).toFixed(4)})` : "";
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const [highlightFirst, highlightRest] = splitFirstWord(data?.featuresTitleHighlight);

  return (
    <section className="bg-[#F7F8F6] px-[2rem] pt-[8rem] pb-[8rem] lg:px-0 lg:pt-[15rem] lg:pb-0">
      <div className="mx-auto flex w-full max-w-[150rem] flex-col items-center gap-[4rem] lg:gap-[5rem]">
        <div className="flex w-full max-w-[113.625rem] flex-col items-center gap-[2.5rem] text-center lg:gap-[4.125rem]">
          <Chip text={data?.featuresChip} />
          <div className="flex w-full flex-col items-center gap-[2rem] lg:gap-[4.125rem]">
            <h2
              ref={revealLines}
              data-reveal
              className="font-owners text-[4.5rem] leading-[1.055] font-medium tracking-[.01em] text-black uppercase lg:text-[7.5rem] lg:leading-[1.08] lg:tracking-[.02em]">
              <RevealWords text={data?.featuresTitle} />{" "}
              {data?.featuresTitleHighlight && (
                <>
                  {/* phones: one box per line (Figma), upright */}
                  <span className="lg:hidden">
                    <Highlight>
                      <RevealWords text={highlightFirst} />
                    </Highlight>
                    {highlightRest && (
                      <>
                        {" "}
                        <Highlight>
                          <RevealWords text={highlightRest} />
                        </Highlight>
                      </>
                    )}
                  </span>
                  <span className="hidden lg:inline">
                    <Highlight className="italic">
                      <RevealWords text={data.featuresTitleHighlight} />
                    </Highlight>
                  </span>
                </>
              )}
            </h2>
            {data?.featuresText && (
              <FadeUp className="w-full max-w-[63.375rem]">
                <p className="font-geist text-[2.25rem] leading-[1.4] font-medium tracking-[-.02em] text-black/60 lg:tracking-[-.03em]">
                  {data.featuresText}
                </p>
              </FadeUp>
            )}
          </div>
        </div>

        <div
          ref={cardsRef}
          className="flex w-full flex-col items-center gap-[2rem] lg:gap-[6rem]">
          {data?.featuresCards?.map((card, i) => (
            <article
              key={i}
              className="grid w-full origin-center grid-cols-1 rounded-[7px] border border-black/[.06] bg-white p-[.75rem] will-change-transform lg:grid-cols-[499fr_683fr] lg:gap-[1rem] lg:py-[.75rem] lg:pr-[.75rem] lg:pl-[.125rem]">
              <div className="flex flex-col justify-between gap-[3rem] px-[2.25rem] pt-[2.25rem] pb-[3rem] lg:h-[63.875rem] lg:gap-[5rem] lg:p-[5rem]">
                <p className="font-geistmono text-[7rem] leading-none font-medium tracking-[-.09em] text-black/[.08] lg:text-[12.5rem] lg:leading-[.75]">
                  {pad(i + 1)}
                </p>
                <div className="flex flex-col gap-[1.5rem] lg:gap-[2rem]">
                  <h3
                    ref={revealLines}
                    data-reveal
                    className="font-owners text-[3.5rem] leading-[1.1] font-medium tracking-[.01em] whitespace-pre-line text-[#0B0C0D] lg:text-[5rem] lg:tracking-[.025em]">
                    <RevealWords text={card?.title} />
                  </h3>
                  {card?.text && (
                    <p className="font-geist text-[2rem] leading-[1.4] text-[#0B0C0D]/70 lg:max-w-[52rem] lg:whitespace-pre-line">
                      {card.text}
                    </p>
                  )}
                </div>
              </div>
              <CardVisual
                card={card}
                target={data?.eventDate}
              />
            </article>
          ))}
        </div>

        {data?.featuresCTA && (
          <FadeUp className="-mt-[1rem] flex w-full justify-center lg:mt-0">
            <CTACornerButton data={data.featuresCTA} />
          </FadeUp>
        )}
      </div>
    </section>
  );
}
